import * as React from 'react'
import styled from 'styled-components'

import { styledComponentWithProps } from '../../Styled/Shared'
import themeObj from '../../../theme/defaultTheme'

export enum Side {
  Top,
  Bottom,
  Left,
  Right
}

type Coords = {
  x: number
  y: number
}

export type TooltipProps = {
  pos: Coords
  text: string
  show: boolean
  width?: number // default: auto
  maxWidth?: number // default: 350px
  side?: Side // default: top
}

type Props = {
  tooltip: TooltipProps
}

type StyleProps = {
  arrowProps: ArrowStyleProps
  ttProps: TTStyleProps
}

type TTStyleProps = {
  top: string
  left: string
  width: string
  maxWidth: string
  transform: string
}

type ArrowStyleProps = {
  top: string
  left: string
  marginLeft: string
  marginTop: string
  borderColor: string
}

const TooltipWrapper = styledComponentWithProps<StyleProps>(styled.div)`
  background: ${({ theme }) => theme.uiElements.tooltip.background};
  color: ${({ theme }) => theme.uiElements.tooltip.color};
  position: fixed;
  top: ${({ ttProps }) => ttProps.top};
  left: ${({ ttProps }) => ttProps.left};

  width: ${({ ttProps }) => ttProps.width};
  max-width: ${({ ttProps }) => ttProps.maxWidth};
  padding: 10px;
  font-size: 13px;
  line-height: 17px;
  z-index: 10000;
  transform: ${({ ttProps }) => ttProps.transform};
  box-shadow: 0 0 19px rgba(0, 0, 0, 0.18);
  text-align: left;
  pointer-events: none;

  .tooltiptext {
    text-align: left;
    font-weight: normal;
  }

  .tooltiptext::after {
    /* Draws the arrow for the tooltip */
    content: '';
    position: absolute;
    top: ${({ arrowProps }) => arrowProps.top};
    left: ${({ arrowProps }) => arrowProps.left};
    margin-left: ${({ arrowProps }) => arrowProps.marginLeft};
    margin-top: ${({ arrowProps }) => arrowProps.marginTop};
    border-width: 5px;
    border-style: solid;
    border-color: ${({ arrowProps }) => arrowProps.borderColor}
  }
`

type State = {
  showTip: boolean
}

export default class Tooltip extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { showTip: false }
  }

  componentDidMount() {
    this.addScrollListeners()
  }

  componentWillUnmount() {
    this.removeScrollListeners()
  }

  addScrollListeners() {
    window.addEventListener('scroll', this.hideTooltip)
    const workspaceCaseList = document.getElementById('workspace-case-list')
    if (workspaceCaseList) {
      workspaceCaseList.addEventListener('scroll', this.hideTooltip)
    }
    const resultsScrollView = document.getElementById('results-scrollview')
    if (resultsScrollView) {
      resultsScrollView.addEventListener('scroll', this.hideTooltip)
    }
  }

  removeScrollListeners() {
    window.removeEventListener('scroll', this.hideTooltip)
    const workspaceCaseList = document.getElementById('workspace-case-list')
    if (workspaceCaseList) {
      workspaceCaseList.removeEventListener('scroll', this.hideTooltip)
    }
    const resultsScrollView = document.getElementById('results-scrollview')
    if (resultsScrollView) {
      resultsScrollView.removeEventListener('scroll', this.hideTooltip)
    }
  }

  hideTooltip = () => {
    if (this.props.tooltip.show) {
      this.setState({ showTip: false })
    }
  }

  componentWillReceiveProps(newProps: Props) {
    this.setState({
      showTip: newProps.tooltip.show ? newProps.tooltip.show : false
    })
  }

  getOffsets(pos: Coords, side?: Side) {
    let top = pos.y
    let left = pos.x
    let transform = ''
    switch (side) {
      case Side.Top:
        top -= 10
        transform = 'translate(-50%, -100%)'
        break
      case Side.Bottom:
        top += 10
        transform = 'translate(-50%, 0)'
        break
      case Side.Left:
        left -= 10
        transform = 'translate(-100%, -50%)'
        break
      case Side.Right:
        left += 10
        transform = 'translate(0, -50%)'
        break
      default:
        top -= 10
        transform = 'translate(-50%, -100%)'
        break
    }
    return {
      top: top,
      left: left,
      transform: transform
    }
  }

  getArrowProps(side?: Side) {
    const props = {
      top: '0',
      left: '0',
      borderColor: '',
      marginLeft: '',
      marginTop: ''
    }
    const arrowColor = themeObj.uiElements.tooltip.background
    switch (side) {
      case Side.Left:
        props.top = '50%'
        props.left = '100%'
        props.marginTop = '-5px'
        props.borderColor = 'transparent transparent transparent' + arrowColor
        break
      case Side.Right:
        props.top = '50%'
        props.marginTop = '-5px'
        props.marginLeft = '-10px'
        props.borderColor =
          'transparent ' + arrowColor + ' transparent transparent'
        break
      case Side.Top:
        props.top = '100%'
        props.left = '50%'
        props.marginLeft = '-5px'
        props.borderColor = arrowColor + ' transparent transparent transparent'
        break
      case Side.Bottom:
        props.left = '50%'
        props.marginTop = '-10px'
        props.marginLeft = '-5px'
        props.borderColor =
          'transparent transparent ' + arrowColor + ' transparent'
        break
      default:
        props.top = '100%'
        props.left = '50%'
        props.marginLeft = '-5px'
        props.borderColor = arrowColor + ' transparent transparent transparent'
        break
    }
    return props
  }

  /*
    Explanation to the conditions:
    We won't show the tooltip in any case if:
      - the tt prop hasn't been defined
      - showTip is false (the view has been scrolled or if not, tooltip.show in props is false)
      - the user has toggled tooltips off (userTooltipsOn is false)
      - the tooltip doesn't have a defined text for it
  */

  showTooltip = (tt: TooltipProps) => {
    const { showTip } = this.state
    if (!tt) {
      return false
    }
    // If there's no tooltip boolean in LS, it means the user hasn't toggled it
    // and it's on by default.
    const ttStorageItem = localStorage.getItem('tooltips')
    const userTooltipsOn = ttStorageItem ? JSON.parse(ttStorageItem) : true
    const hasText = tt.text && tt.text.length > 0
    return showTip && userTooltipsOn && hasText
  }

  render() {
    const tt = this.props.tooltip
    if (!this.showTooltip(tt)) {
      return null
    }
    const { pos, text, width, maxWidth, side } = tt
    const offsets = this.getOffsets(pos, side)
    const ttProps = {
      top: offsets.top + 'px',
      left: offsets.left + 'px',
      width: width ? width + 'px' : 'auto',
      maxWidth: maxWidth ? maxWidth + 'px' : '350px',
      transform: offsets.transform
    }
    return (
      <TooltipWrapper ttProps={ttProps} arrowProps={this.getArrowProps(side)}>
        <span className="tooltiptext">{text}</span>
      </TooltipWrapper>
    )
  }
}



// WEBPACK FOOTER //
// ./src/Components/Shared/Tooltip/index.tsx